/**
 * Locale negotiation.
 *
 * Priority: explicit user preference (stored on the account) > Accept-Language > tenant default
 * > platform default. This module implements the Accept-Language step; the others are simple
 * lookups performed by the caller.
 */

interface LanguageRange {
  readonly tag: string;
  readonly quality: number;
}

export function parseAcceptLanguage(header: string | undefined): LanguageRange[] {
  if (header === undefined || header.trim().length === 0) return [];

  return header
    .split(',')
    .map((part): LanguageRange | null => {
      const [tag, ...parameters] = part.trim().split(';');
      if (tag === undefined || tag.length === 0) return null;

      const qParameter = parameters.find((parameter) => parameter.trim().startsWith('q='));
      const quality =
        qParameter === undefined ? 1 : Number.parseFloat(qParameter.split('=')[1] ?? '1');

      return {
        tag: tag.trim(),
        quality: Number.isFinite(quality) ? Math.min(Math.max(quality, 0), 1) : 0,
      };
    })
    .filter((range): range is LanguageRange => range !== null && range.quality > 0)
    .sort((a, b) => b.quality - a.quality);
}

/**
 * Picks the best supported locale. Matching is case-insensitive and falls back from a regional
 * tag to its base language, so a browser asking for `nl-BE` is served the `nl` catalogue.
 */
export function negotiateLocale(
  acceptLanguage: string | undefined,
  supported: readonly string[],
  defaultLocale: string,
): string {
  const supportedByLower = new Map(supported.map((locale) => [locale.toLowerCase(), locale]));

  for (const range of parseAcceptLanguage(acceptLanguage)) {
    const lower = range.tag.toLowerCase();

    if (lower === '*') return defaultLocale;

    const exact = supportedByLower.get(lower);
    if (exact !== undefined) return exact;

    const base = lower.split('-')[0] ?? lower;
    const baseMatch = supportedByLower.get(base);
    if (baseMatch !== undefined) return baseMatch;

    // A request for `nl` should also match a supported `nl-NL`.
    const regional = supported.find((locale) => locale.toLowerCase().startsWith(`${base}-`));
    if (regional !== undefined) return regional;
  }

  return defaultLocale;
}
